import { PaginatedResponse } from '@shared/models/paginated-response.model';

/**
 * DTO para tipos de servicios divinos con nombres legibles
 */
export interface DivineServiceTypeDto {
  value: number;
  name: string;
  displayName: string;
  offeringRequired: boolean;
}

/**
 * DTO con información completa de un servicio divino.
 * Se usa tanto para detalles como para listar en tabla.
 */
export interface DivineServiceDetailDto {
  id: string;
  startAt: string; 
  endAt: string; 
  divineServiceTypeId: number;
  typeName?: string;
  divineServiceTypeName?: string;
  bibleText?: string;
  memberInCharge?: string;
  location?: string;
  organizationUnitId: string;
  organizationUnitName?: string;
  attendeeCount?: number;
  totalMinors: number;
  totalAdults: number;
  notes?: string;
  offeringsARS: number;
  offeringsUSD: number;
  visitsAdults?: number;
  visitsMinors?: number;
  newAdults?: number;
  isClosed?: boolean;
  closedAtUtc?: string;
  newMinors?: number;
}

/**
 * DTO para crear un servicio divino
 */
export interface CreateDivineServiceDto {
  startAt: string;
  endAt: string;
  divineServiceTypeId: number;
  bibleText: string; // Texto bíblico (requerido)
  memberInCharge?: string;
  location?: string;
  organizationUnitId: string;
  totalMinors: number; // Total de menores
  totalAdults: number; // Total de adultos
  notes?: string; // Notas adicionales
  offeringsARS: number; // Ofrendas en ARS
  offeringsUSD: number; // Ofrendas en USD
  visitsAdults?: number; // Visitas - mayores
  visitsMinors?: number; // Visitas - menores
  newAdults?: number; // Almas nuevas - mayores
  newMinors?: number; // Almas nuevas - menores
}

/**
 * DTO para actualizar un servicio divino
 */
export interface UpdateDivineServiceDto {
  startAt: string;
  endAt: string;
  divineServiceTypeId: number;
  bibleText: string; // Texto bíblico (requerido)
  memberInCharge?: string;
  location?: string;
  organizationUnitId: string;
  totalMinors: number; // Total de menores
  totalAdults: number; // Total de adultos
  notes?: string; // Notas adicionales
  offeringsARS: number; // Ofrendas en ARS
  offeringsUSD: number; // Ofrendas en USD
  visitsAdults?: number; // Visitas - mayores
  visitsMinors?: number; // Visitas - menores
  newAdults?: number; // Almas nuevas - mayores
  newMinors?: number; // Almas nuevas - menores
}

/**
 * DTO para un miembro con información de asistencia a un servicio divino
 * Devuelto desde el endpoint GET /divine-services/{id}/members-attendance
 */
export interface MemberAttendanceDto {
  personId: string;
  firstName: string;
  lastName: string;
  email?: string;
  documentNumber?: string;
  ageStatus?: string;
  isSealed: boolean;
  attended: boolean;
  attendanceCategory?: 'Member' | 'Visitor';
  isVisitor?: boolean;
  notes?: string;
}

/**
 * DTO para operaciones bulk de asistencia (marcar/desmarcar múltiples personas)
 */
export interface BulkAttendanceDto {
  personIds: string[];
  notes?: string;
}

/**
 * DTO para listar asistencia a un servicio
 */
export interface AttendanceListDto {
  id: string;
  divineServiceId: string;
  attendantId: string;
  attendantName: string;
  attendantEmail?: string;
  notes?: string;
}

/**
 * DTO con detalles de asistencia
 */
export interface AttendanceDetailDto {
  id: string;
  divineServiceId: string;
  attendantId: string;
  attendantName: string;
  attendantEmail?: string;
  notes?: string;
  createdAtUtc: string;
  updatedAtUtc?: string;
}

/**
 * Respuesta de asistencia desde el backend (endpoint antiguo)
 * Contiene la lista paginada de asistentes a un servicio divino
 */
export interface AttendanceResponseDto {
  divineServiceId: string;
  attendees: PaginatedResponse<MemberAttendanceDto>;
}

/**
 * Respuesta paginada de miembros con asistencia
 * Devuelto por el nuevo endpoint GET /divine-services/{id}/members-attendance
 * Este es el endpoint recomendado para la página de asistencia
 */
export interface AttendanceRosterSummaryDto {
  sealedAdults: number;
  sealedMinors: number;
  unsealedAdults: number;
  unsealedMinors: number;
  nominalVisitsAdults: number;
  nominalVisitsMinors: number;
  visitsAdults: number;
  visitsMinors: number;
  newAdults: number;
  newMinors: number;
  totalAdults: number;
  totalMinors: number;
}

export interface MembersAttendancePagedResponse extends PaginatedResponse<MemberAttendanceDto> {
  summary: AttendanceRosterSummaryDto;
}

/**
 * DTO para crear o actualizar asistencia
 */
export interface CreateOrUpdateAttendanceDto {
  divineServiceId: string;
  attendantId: string;
  notes?: string;
}

/**
 * Interface que define la estructura de datos para mostrar totales de asistencia por categorías
 * Agrupa: Miembros, Visitas, Almas Nuevas y Totales (cada uno con Adultos y Menores)
 */
export interface TotalsByCategoryData {
  sealedAdults: number;
  sealedMinors: number;
  unsealedAdults: number;
  unsealedMinors: number;
  visitsAdults: number;
  visitsMinors: number;
  newAdults: number;
  newMinors: number;
}

/**
 * Interface para los totales de visitas y almas nuevas desde el componente de edición
 */
export interface AttendanceDetailTotals {
  visitsAdults: number;
  visitsMinors: number;
  newAdults: number;
  newMinors: number;
}

/**
 * Respuesta paginada de servicios divinos
 * Utiliza el PaginatedResponse<T> genérico que matchea con el backend
 */
export type DivineServicePagedResponse = PaginatedResponse<DivineServiceDetailDto>;

/**
 * Utilidad para obtener el nombre del tipo de servicio
 * @param typeId - ID del tipo de servicio
 * @param types - Array de tipos de servicios disponibles
 * @returns Nombre del tipo de servicio
 */
export function getDivineServiceTypeName(typeId: number, types: DivineServiceTypeDto[]): string {
  const type = types.find(t => t.value === typeId);
  return type?.name || 'Desconocido';
}
